import React, { useState, useEffect, useRef, useCallback } from 'react';
import Chip from '@mui/material/Chip';
import './UploadMedia.css';
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Notification from '../../Notification/Notification';
import { useWebSocket } from '../../WebsocketComponent/WebSocketProvider';
import CreateCategories from '../../CategoryManagement/CreateCategories/CreateCategories';

const apiUrl = process.env.REACT_APP_API_URL;

function UploadMedia() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [mediaType, setMediaType] = useState('image');
  const [categories, setCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [selectedChildCategories, setSelectedChildCategories] = useState([]);
  const [error, setError] = useState('');
  const [addCategory, setAddCategory] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);

  const [token] = useState(localStorage.getItem('token') || '');
  const { uploadProgress, clientId } = useWebSocket();

  // Új állapot a spinnerhez / gomb tiltáshoz
  const [isUploading, setIsUploading] = useState(false);

  const [localUploadStatuses, setLocalUploadStatuses] = useState({});
  const [localIsUploading, setLocalIsUploading] = useState(false);
  const [localCurrentlyUploadingFile, setLocalCurrentlyUploadingFile] = useState(null);
  const [localWasUploading, setLocalWasUploading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [notificationType, setNotificationType] = useState(null);
  const [refresh, setRefresh] = useState(false);

  const fetchCategories = useCallback(() => {
    fetch(`${apiUrl}/categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => response.json())
      .then((data) => {
        setCategories(data);
        setParentCategories(data.filter((cat) => cat.is_parent));
      })
      .catch((error) => console.error("Error fetching categories:", error));
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, refresh]);

  const handleAddClick = () => setAddCategory(true);
  const handleClose = () => { setAddCategory(false); setRefresh(prev => !prev); };

  useEffect(() => {
    if (!uploadProgress) return;

    setLocalUploadStatuses(uploadProgress.fileStatuses);
    setLocalIsUploading(uploadProgress.processedFiles < uploadProgress.totalFiles);
    setLocalCurrentlyUploadingFile(
      Object.keys(uploadProgress.fileStatuses).find(
        filename => uploadProgress.fileStatuses[filename].status === 'uploading'
      ) || null
    );

    // Gomb / spinner állapot WebSocket alapján
    setIsUploading(uploadProgress.processedFiles < uploadProgress.totalFiles);

    if (uploadProgress.totalFiles > 0 && uploadProgress.overallProgress === 100 && !localWasUploading) {
      setLocalWasUploading(true);
      setIsUploading(false);

      const { successful, duplicates, failed } = uploadProgress;
      if (successful > 0 && failed === 0 && duplicates === 0) {
        setNotificationMessage('Minden fájl sikeresen feltöltve!');
        setNotificationType('success');
      } else {
        setNotificationMessage(
          `Feltöltés befejezve: ${successful} sikeres, ${duplicates} duplikált, ${failed} hibás.`
        );
        setNotificationType(failed > 0 ? 'error' : 'warning');
      }

      const successfullyUploadedFileNames = new Set(
        Object.keys(uploadProgress.fileStatuses).filter(
          filename => uploadProgress.fileStatuses[filename].status === 'success'
        )
      );
      setSelectedFiles(prevFiles =>
        prevFiles.filter(file => !successfullyUploadedFileNames.has(file.name))
      );

      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    if (uploadProgress.totalFiles === 0 && localWasUploading) {
      setNotificationMessage(null);
      setNotificationType(null);
      setLocalWasUploading(false);
    }
  }, [uploadProgress]);

  const handleParentCategoryChange = (e) => {
    const parentId = e.target.value ? parseInt(e.target.value) : e.target.value;
    setSelectedParentCategory(parentId);
    setSelectedChildCategories([]);
    setChildCategories(categories.filter(category => !category.is_parent));
  };

  const handleChipClick = (categoryId) => {
    if (selectedChildCategories.includes(categoryId)) {
      setSelectedChildCategories(prev => prev.filter(id => id !== categoryId));
    } else {
      setSelectedChildCategories(prev => [...prev, categoryId]);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
    setLocalWasUploading(false);
    setLocalUploadStatuses({});
    setNotificationMessage(null);
    setNotificationType(null);
  };

  const handleToggle = () => {
    setMediaType(prev => (prev === 'image' ? 'video' : 'image'));
    setSelectedFiles([]);
    fileInputRef.current.value = "";
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setIsUploading(true); // Feltöltés elindult

    const categoriesToUpload = [...selectedChildCategories];
    if (selectedParentCategory) categoriesToUpload.unshift(selectedParentCategory);

    const formData = new FormData();
    selectedFiles.forEach(file => formData.append('files', file));
    formData.append('imageType', mediaType);
    formData.append('categories', categoriesToUpload.join(','));
    formData.append('userId', localStorage.getItem('id'));

    try {
      const response = await fetch(`${apiUrl}/images`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Upload-Client-Id': clientId,
        },
      });
      const resultData = await response.json();
      if (!response.ok) setError(resultData.message || 'Feltöltés sikertelen a szerver oldalon.');
    } catch (uploadError) {
      console.error('Upload batch error:', uploadError);
      setError(uploadError.message || 'Hálózati hiba a feltöltés során.');
    }
    // A gomb / spinner állapotot a WebSocket állítja vissza, ne itt
  };

  const handleCloseNotification = () => { setNotificationMessage(null); setNotificationType(null); };
  const handleRemoveFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
        if (updatedFiles.length > 0) {
          const dt = new DataTransfer();
          updatedFiles.forEach((file) => dt.items.add(file));
          fileInputRef.current.files = dt.files;
        }
      }
    }, 0);
  };

  return (
    <div className="upload-media">
      {addCategory ? (
        <CreateCategories onCancel={handleClose} onSaveSuccess={handleClose} />
      ) : (
        <>
          <h2>Media Feltöltés</h2>
          <form onSubmit={handleUpload}>
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={mediaType === 'video'}
                  onChange={handleToggle}
                />
                Videó
              </label>
            </div>
            <div>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileChange}
              />
              <div className="parent-category-container">
                <label>Szülőkategória:</label>
                <div className="select-with-button">
                  <select
                    value={selectedParentCategory}
                    onChange={handleParentCategoryChange}
                  >
                    <option value="">Válassz szülőkategóriát</option>
                    {parentCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <div>
                    <IconButton className="add-category-btn" onClick={handleAddClick} disabled={addCategory}>
                      <AddIcon />
                    </IconButton>


                  </div>
                </div>
              </div>


              {selectedParentCategory && (
                <div className="parent-category-container">
                  <label>Alkategóriák:</label>
                  <div className="mt-3 align-items-center" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {childCategories.map(category => (
                      <Chip
                        key={category.id}
                        label={category.name}
                        onClick={() => handleChipClick(category.id)}
                        style={{
                          backgroundColor: selectedChildCategories.includes(category.id) ? '#1976d2' : '#e0e0e0',
                          color: selectedChildCategories.includes(category.id) ? '#fff' : '#000'
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="preview">
                {selectedFiles.length > 0 && (
                  <div className="preview-container" ref={previewContainerRef}>
                    {selectedFiles.map((file, index) => (
                      <div
                        className={`preview-item ${localCurrentlyUploadingFile === file.name ? 'uploading-active' : ''}`}
                        key={file.name}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        {mediaType === 'image' ? (
                          <div className="image-icon-wrapper">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`preview-${index}`}
                              className="preview-image"
                              title={file.name}
                            />
                          </div>

                        ) : mediaType === 'video' ? (
                          <div className="video-icon-wrapper">

                            <i title={file.name} className="bi bi-play-circle-fill video-play-icon" alt={`preview-${index}`} />

                          </div>

                        ) : null}
                        <span className="file-name">{file.name}</span>
                        {hoveredIndex === index && (
                          <button
                            className="remove-button"
                            onClick={() => handleRemoveFile(index)}
                          >
                            X
                          </button>
                        )}
                        {localUploadStatuses[file.name] && (
                          <div className={`upload-status ${localUploadStatuses[file.name].status}`}>
                            {localUploadStatuses[file.name].status === 'uploading' && (
                              <div className="progress-bar-container">
                                <div
                                  className="progress-bar"
                                  style={{ width: `${localUploadStatuses[file.name].progress}%` }}
                                ></div>
                              </div>
                            )}
                            <span>{localUploadStatuses[file.name].message || localUploadStatuses[file.name].status}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="upload-actions">
              {selectedFiles.length > 0 && (
                <>
                  <button
                    className="upload-btn"
                    type="submit"
                    disabled={isUploading || localIsUploading || selectedParentCategory.length === 0}
                  >
                    {(isUploading || localIsUploading) ? (
                      <span className="spinner"></span>
                    ) : (
                      selectedFiles.length > 0 &&
                        selectedParentCategory.length > 0 &&
                        !localIsUploading && localWasUploading
                        ? 'Feltöltés ismétlése'
                        : 'Feltöltés'
                    )
                    }
                  </button>

                  <button
                    className="clear-list-btn"
                    type="button"
                    onClick={() => {
                      setLocalWasUploading(false);
                      setSelectedFiles([]);
                      setLocalUploadStatuses({});
                      setSelectedParentCategory('');
                      setSelectedChildCategories([]);
                      setMediaType('image');
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    disabled={isUploading || localIsUploading}
                  >
                    Lista törlése
                  </button>
                </>
              )}
            </div>


            {error && <p style={{ color: 'red' }}>{error}</p>}
            <Notification
              message={notificationMessage}
              type={notificationType}
              onClose={handleCloseNotification}
            />
          </form>
        </>
      )}
    </div>
  );
}

export default UploadMedia;
